/**
 * SHX heading highlights — hand-drawn accents (marker, tilted marker,
 * underline, circle, and pencil-textured variants) drawn in when they scroll
 * into view. Own SVG paths and texture filter; concept-compatible with the
 * Stretch theme, implemented independently.
 *
 * How highlights are assigned (always PER HEADING, never globally):
 * Shrine's "Heading words highlight 2" select (vendor heading block +
 * section-title snippet) — extended with the SHX styles — renders italicized
 * words as `<em class="shx-marker">`, `<em class="shx-circle">`, etc.
 * SHX sections (Dynamic grid heading, Featured collection) emit the same
 * markup. This script finds those elements, injects the SVG strokes where the
 * style needs them, and arms the draw-in observer. The accent color comes from
 * that heading's own color setting via --shx-hl-accent.
 */

const EFFECTS = [
  'marker',
  'marker-tilted',
  'underline',
  'underline-pencil',
  'circle',
  'circle-pencil',
];
const SELECTOR = EFFECTS.map((e) => `.shx-${e}:not([data-shx-hl])`).join(', ');

let filterInjected = false;

/** Rough pencil-texture filter (standard feTurbulence displacement technique). */
function ensurePencilFilter() {
  if (filterInjected) return;
  filterInjected = true;
  const holder = document.createElement('div');
  holder.setAttribute('aria-hidden', 'true');
  holder.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;';
  holder.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg"><defs><filter id="shx-pencil" x="-8%" y="-8%" width="116%" height="116%">' +
    '<feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" seed="7" result="n"/>' +
    '<feDisplacementMap in="SourceGraphic" in2="n" scale="3" xChannelSelector="R" yChannelSelector="G"/>' +
    '</filter></defs></svg>';
  document.body.appendChild(holder);
}

/** Own hand-drawn paths (authored freehand on their viewBoxes). */
function svgFor(style) {
  const pencil = style.includes('pencil');
  if (pencil) ensurePencilFilter();
  const filterAttr = pencil ? ' filter="url(#shx-pencil)"' : '';
  const width = pencil ? 3 : 2;
  if (style.startsWith('underline')) {
    return (
      '<svg class="shx-hl__svg" viewBox="0 0 200 16" preserveAspectRatio="none" aria-hidden="true" focusable="false">' +
      `<path pathLength="1"${filterAttr} stroke-width="${width}" d="M3 9 C 40 4, 80 12, 118 8 S 180 5, 197 8 M 30 12 C 80 15, 130 10, 172 12"/>` +
      '</svg>'
    );
  }
  // circle: a loose hand loop that slightly overlaps itself
  return (
    '<svg class="shx-hl__svg" viewBox="0 0 220 100" preserveAspectRatio="none" aria-hidden="true" focusable="false">' +
    `<path pathLength="1"${filterAttr} stroke-width="${width}" d="M150 8 C 80 -4, 10 14, 8 48 C 6 82, 70 98, 122 95 C 176 92, 216 76, 213 46 C 210 18, 160 4, 96 6"/>` +
    '</svg>'
  );
}

const observer = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-hl-visible');
      observer.unobserve(entry.target);
    });
  },
  { rootMargin: '0px 0px -12% 0px' }
);

function apply() {
  document.querySelectorAll(SELECTOR).forEach((el) => {
    const style = EFFECTS.find((e) => el.classList.contains(`shx-${e}`));
    if (!style) return;
    el.dataset.shxHl = 'true';
    el.classList.add('shx-hl', `shx-hl--${style}`);
    if (style.includes('pencil')) ensurePencilFilter();
    if (
      (style.startsWith('underline') || style.startsWith('circle')) &&
      !el.querySelector('.shx-hl__svg')
    ) {
      el.insertAdjacentHTML('beforeend', svgFor(style));
    }
    observer.observe(el);
  });
}

apply();
document.addEventListener('shopify:section:load', apply);
